import { useState } from "react";
import axios from "axios";
import { API, fmtErr } from "../lib/pos";

export function StationAdmin({ stations, categories, refresh }) {
  const [editing, setEditing] = useState(null); // {id, name, assigned_categories}
  const [resetInfo, setResetInfo] = useState(null); // {station_name, email, new_password}
  const [resetBusy, setResetBusy] = useState(null); // station_id
  const [copied, setCopied] = useState(false);
  const openEdit = (s) => setEditing({ ...s, _original_email: s.email || "", assigned_categories: [...(s.assigned_categories || [])], gst_number: s.gst_number || "", description: s.description || "", email: s.email || "", receive_daily_summary: !!s.receive_daily_summary });
  const save = async () => {
    try {
      const body = {
        name: editing.name,
        description: (editing.description || "").trim(),
        assigned_categories: editing.assigned_categories,
        gst_number: (editing.gst_number || "").trim(),
        receive_daily_summary: !!editing.receive_daily_summary,
      };
      const newEmail = (editing.email || "").trim().toLowerCase();
      // Only send email if it actually changed — avoids spurious 409 conflict checks
      if (newEmail && newEmail !== (editing._original_email || "").toLowerCase()) {
        body.email = newEmail;
      }
      await axios.patch(`${API}/stations/${editing.id}`, body);
      setEditing(null); refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const toggleDailyReport = async (s) => {
    try {
      await axios.patch(`${API}/stations/${s.id}`, { receive_daily_summary: !s.receive_daily_summary });
      refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const toggleCat = (c) => setEditing((prev) => {
    const has = prev.assigned_categories.includes(c);
    return { ...prev, assigned_categories: has ? prev.assigned_categories.filter((x) => x !== c) : [...prev.assigned_categories, c] };
  });
  const resetPw = async (s) => {
    if (!window.confirm(`Reset password for ${s.name}? The old password will stop working immediately.`)) return;
    setResetBusy(s.id); setCopied(false);
    try {
      const r = await axios.post(`${API}/stations/${s.id}/reset-password`);
      setResetInfo(r.data);
    } catch (e) { alert(fmtErr(e)); }
    finally { setResetBusy(null); }
  };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(resetInfo.new_password); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { setCopied(false); }
  };
  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / STATIONS</p>
          <h1>Station admin</h1>
          <p className="muted">Assign categories and reset passwords for each of your 15 stations.</p>
        </div>
      </div>
      <div className="stations-table" data-testid="stations-table">
        <div className="stations-table-head">
          <span>Station</span>
          <span>Email</span>
          <span>Categories</span>
          <span>Daily report</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {stations.map((s) => {
          const isOff = s.active === false;
          const cats = s.assigned_categories || [];
          const catsLabel = cats.length ? `${cats.length} categor${cats.length === 1 ? "y" : "ies"}` : "All (Expo)";
          return (
            <div className={`stations-row ${isOff ? "station-off" : ""}`} key={s.id} data-testid={`station-row-${s.id}`}>
              <div className="stations-cell stations-cell-name">
                <b>{s.name}</b>
                {isOff && <em className="off-tag">DISABLED</em>}
                {s.gst_number && <small className="station-gst" data-testid={`station-${s.id}-gst`}>GST · {s.gst_number}</small>}
              </div>
              <div className="stations-cell" title={s.email}><span className="mono-email">{s.email || "—"}</span></div>
              <div className="stations-cell">
                <span className="cat-chip-count" title={cats.length ? cats.join(", ") : "All categories (Expo)"}>{catsLabel}</span>
              </div>
              <div className="stations-cell">
                <label
                  className="daily-report-toggle"
                  data-testid={`station-${s.id}-daily-report-toggle`}
                  title="Send this station a daily sales summary email at 08:00 IST"
                >
                  <input
                    type="checkbox"
                    checked={!!s.receive_daily_summary}
                    onChange={() => toggleDailyReport(s)}
                    data-testid={`station-${s.id}-daily-report-checkbox`}
                  />
                  <span>{s.receive_daily_summary ? "ON" : "OFF"}</span>
                </label>
              </div>
              <div className="stations-cell">
                <button
                  data-testid={`station-${s.id}-active-toggle`}
                  className={`toggle ${isOff ? "off" : "on"}`}
                  onClick={async () => {
                    try { await axios.patch(`${API}/stations/${s.id}/active`, { active: isOff }); refresh(); }
                    catch (e) { alert(fmtErr(e)); }
                  }}
                  title={isOff ? "Enable — station receives tickets again" : "Disable — station stops seeing new items"}
                ><i /><span>{isOff ? "OFF" : "ON"}</span></button>
              </div>
              <div className="stations-cell stations-cell-actions">
                <button data-testid={`reset-station-${s.id}-button`} className="outline sm" disabled={resetBusy === s.id} onClick={() => resetPw(s)}>
                  {resetBusy === s.id ? "…" : "Reset PW"}
                </button>
                <button data-testid={`edit-station-${s.id}-button`} className="primary sm" onClick={() => openEdit(s)}>Edit</button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setEditing(null)}>
          <div className="modal" style={{ width: "min(560px, 92vw)" }}>
            <div className="section-line">
              <h2>Edit station</h2>
              <button data-testid="close-station-modal-button" onClick={() => setEditing(null)}>×</button>
            </div>
            <label className="field">Name
              <input data-testid="station-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </label>
            <label className="field">Login &amp; daily-report email
              <input
                data-testid="station-email-input"
                type="email"
                value={editing.email || ""}
                autoComplete="off"
                placeholder="station@shimlafoodcourt.com"
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              />
              <small style={{ color: "var(--muted)", fontSize: 10, marginTop: 4, display: "block", letterSpacing: 0 }}>
                Changing this updates both the station&apos;s login email and where the daily report is delivered.
              </small>
            </label>
            <label className="field">Short description <span style={{ color: "var(--muted)", fontWeight: 400, letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· shown to customers on the mobile menu</span>
              <input
                data-testid="station-description-input"
                value={editing.description || ""}
                maxLength={80}
                placeholder="e.g. Charcoal steaks, ribs & burgers"
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </label>
            <label className="field">GST number <span style={{ color: "var(--muted)", fontWeight: 400, letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· optional · shown on bills</span>
              <input
                data-testid="station-gst-input"
                value={editing.gst_number || ""}
                maxLength={20}
                placeholder="e.g. 27AABCS1234A1Z5"
                onChange={(e) => setEditing({ ...editing, gst_number: e.target.value.toUpperCase() })}
              />
            </label>
            <label className="field" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid="station-daily-report-checkbox"
                checked={!!editing.receive_daily_summary}
                onChange={(e) => setEditing({ ...editing, receive_daily_summary: e.target.checked })}
              />
              <div>
                <b style={{ fontSize: 13 }}>Send daily report email</b>
                <p className="muted" style={{ fontSize: 11, margin: "2px 0 0" }}>Delivers yesterday&apos;s sales summary to this station&apos;s email address at 08:00 IST.</p>
              </div>
            </label>
            <p className="muted" style={{ fontSize: 11, marginTop: 4, marginBottom: 12 }}>
              Category assignments are managed on the <b style={{ color: "#fff" }}>Categories</b> page (each category maps to exactly one station).
            </p>
            <button data-testid="save-station-button" className="primary wide" onClick={save}>Save changes</button>
          </div>
        </div>
      )}

      {resetInfo && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setResetInfo(null)}>
          <div className="modal" style={{ width: "min(480px, 92vw)" }}>
            <div className="section-line">
              <h2>New password ready</h2>
              <button data-testid="close-reset-modal-button" onClick={() => setResetInfo(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>Share this once with <b style={{ color: "#fff" }}>{resetInfo.station_name}</b>. We won&apos;t show it again.</p>
            <div className="pw-display">
              <span data-testid="reset-station-email">{resetInfo.email}</span>
              <code data-testid="reset-new-password">{resetInfo.new_password}</code>
            </div>
            <button data-testid="copy-password-button" className="primary wide" onClick={copyPw}>{copied ? "Copied ✓" : "Copy password"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Table Admin (manager) ---------- */
